import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { notificationsInfiniteOptions, qk } from "@/lib/queries";
import {
  invalidateNotifications,
  patchUnread,
  restoreUnread,
} from "@/lib/optimisticNotifications";
import { useInfiniteScroll } from "@/lib/useInfiniteScroll";
import {
  describeNotification,
  markAllNotificationsRead,
  markNotificationRead,
  NOTIFICATION_COLOR,
  timeAgo,
  type NotificationItem,
  type NotificationPage,
} from "@/lib/notifications";

type NotificationsCache = InfiniteData<NotificationPage>;

export function Notifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data,
    isPending,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(notificationsInfiniteOptions());

  const items = data?.pages.flatMap((p) => p.notifications) ?? [];
  const unread = data?.pages[0]?.unread ?? 0;

  // Refresh the feed/preview AND the bell's unread badge (separate keys).
  const invalidate = () => invalidateNotifications(queryClient);

  // Optimistically patch the cached pages (flip readAt, adjust the unread count)
  // AND the shared `qk.unread` badge so the bell reacts instantly too; onSettled
  // reconciles with the server. Returns the previous caches for rollback.
  type PatchCtx = {
    prev: NotificationsCache | undefined;
    prevUnread: number | undefined;
  };
  async function patchCache(
    mapItem: (n: NotificationItem) => NotificationItem,
    nextUnread: (u: number) => number,
  ): Promise<PatchCtx> {
    await queryClient.cancelQueries({ queryKey: qk.notifications });
    const prev = queryClient.getQueryData<NotificationsCache>(qk.notifications);
    queryClient.setQueryData<NotificationsCache>(qk.notifications, (cache) =>
      cache
        ? {
            ...cache,
            pages: cache.pages.map((p) => ({
              ...p,
              unread: Math.max(0, nextUnread(p.unread)),
              notifications: p.notifications.map(mapItem),
            })),
          }
        : cache,
    );
    const prevUnread = patchUnread(queryClient, nextUnread);
    return { prev, prevUnread };
  }

  const rollback = (ctx: PatchCtx | undefined) => {
    if (!ctx) return;
    if (ctx.prev) queryClient.setQueryData(qk.notifications, ctx.prev);
    restoreUnread(queryClient, ctx.prevUnread);
  };

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: () =>
      patchCache(
        (n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() }),
        () => 0,
      ),
    onError: (_e, _v, ctx) => rollback(ctx),
    onSettled: invalidate,
  });
  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onMutate: (id) =>
      patchCache(
        (n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n),
        (u) => u - 1,
      ),
    onError: (_e, _id, ctx) => rollback(ctx),
    onSettled: invalidate,
  });

  const sentinelRef = useInfiniteScroll<HTMLLIElement>({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  function handleClick(n: NotificationItem) {
    if (!n.readAt) markRead.mutate(n.id);
    if (n.taskId) void navigate({ to: "/", search: { task: n.taskId } });
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-xl font-semibold tracking-tight">
            Notifications
          </h1>
          <Button
            variant="outline"
            size="sm"
            disabled={unread === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            Mark all read
          </Button>
        </div>

        {isPending ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Loading…
          </p>
        ) : isError ? (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-8 text-center text-sm text-destructive">
            Couldn't load notifications.
          </p>
        ) : items.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
            You're all caught up.
          </p>
        ) : (
          <ul className="overflow-hidden rounded-lg border border-border">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  onClick={() => handleClick(n)}
                  className={`flex w-full cursor-pointer gap-3 border-b border-border/60 px-4 py-3 text-left text-sm last:border-0 transition-colors hover:bg-muted/40 ${
                    n.readAt ? "" : "bg-muted/30"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className="mt-1.5 size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: NOTIFICATION_COLOR[n.type] }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block leading-snug">
                      {describeNotification(n)}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {timeAgo(n.createdAt)}
                    </span>
                  </span>
                  {!n.readAt && (
                    <span
                      aria-label="Unread"
                      className="mt-1.5 size-2 shrink-0 rounded-full bg-clickup-purple"
                    />
                  )}
                </button>
              </li>
            ))}
            {hasNextPage && <li ref={sentinelRef} className="h-1" />}
          </ul>
        )}
      </main>
    </div>
  );
}

export default Notifications;
